/**
 * Application Entry Point
 * 
 * Initializes the dependency injection container and renders the React application.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { application, getLogger, getPerformanceService } from './core/Application'
import './index.css'
import App from './App.tsx'
import { applyAutoDarkMode } from './lib/auto-darkmode'

/**
 * Bootstrap function to initialize the application
 */
async function bootstrap(): Promise<void> {
  // Apply dark mode detection early to prevent any flashes
  applyAutoDarkMode();
  
  try {
    // Initialize the application and dependency injection container
    await application.initialize()
    
    const logger = getLogger()
    logger.info('Application bootstrap completed', 'bootstrap')

    // Initialize performance monitoring
    if (typeof window !== 'undefined') {
      try {
        const performanceService = getPerformanceService()
        
        // Record initial page load metrics
        window.addEventListener('load', () => {
          setTimeout(() => {
            const summary = performanceService.getPerformanceSummary()
            logger.info('Performance summary', 'performance', {
              webVitals: summary.webVitals,
              slowComponents: summary.slowComponents.slice(0, 3), // Log top 3 slow components
              navigationTime: summary.navigationMetrics
            })
          }, 1000)
        })

        logger.info('Performance monitoring initialized', 'bootstrap')
      } catch (error) {
                 logger.warn('Performance monitoring initialization failed', 'bootstrap', { error: (error as Error).message })
      }
    }

    // Render the React application
    const container = document.getElementById('root')
    if (!container) {
      throw new Error('Root container not found')
    }

    const root = createRoot(container)
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    )

    logger.info('React application rendered', 'bootstrap')

  } catch (error) {
    // Note: Using console.error here is acceptable as the logger may not be initialized
    console.error('Failed to bootstrap application:', error)
    
    // Show error message to user
    const container = document.getElementById('root')
    if (container) {
      container.innerHTML = `
        <div style="
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          font-family: Arial, sans-serif;
          text-align: center;
          color: #721c24;
          background-color: #f8d7da;
          padding: 20px;
        ">
          <div>
            <h1>Application Failed to Load</h1>
            <p>We're sorry, but there was an error loading the application.</p>
            <p style="font-family: monospace; font-size: 14px; margin-top: 20px;">
              ${error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <button 
              onclick="window.location.reload()" 
              style="
                margin-top: 20px;
                padding: 10px 20px;
                background-color: #721c24;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
              "
            >
              Reload Page
            </button>
          </div>
        </div>
      `
    }
  }
}

// Start the application
bootstrap().catch(console.error)
